import React, { useState } from "react";
import { MapPin, Clock, Phone, Copy, Check, Navigation } from "lucide-react";
import {
   ADDRESS,
   HOURS,
   PHONE_DISPLAY,
   PHONE_TEL,
   MAPS_EMBED,
   MAPS_DIRECTIONS,
 } from "@/lib/site";
 import { Reveal, Overline } from "@/components/site/Reveal";
 import { WhatsappButton } from "@/components/site/WhatsappButton";

export const Location = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section
      id="localizacao"
      data-testid="location-section"
      className="relative py-24 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        {/* Left Info Column */}
        <div className="lg:col-span-5">
          <Reveal>
            <Overline index="04">Localização</Overline>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-clinic-ink">
              Venha nos <span className="italic text-clinic-rose font-normal">visitar</span>
            </h2>
          </Reveal>

          <div className="mt-10 space-y-6">
            {/* Address with Copy & Route Actions */}
            <Reveal delay={0.15}>
              <div className="glass-card rounded-2xl border border-clinic-line/80 p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-clinic-pinkSoft text-clinic-rose">
                    <MapPin className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div className="flex-1">
                    <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-muted font-medium">
                      Endereço
                    </p>
                    <p className="mt-1 font-sans text-sm md:text-base text-clinic-ink leading-relaxed font-medium">
                      {ADDRESS}
                    </p>
                    
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        onClick={handleCopyAddress}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-clinic-line bg-clinic-bg px-3 py-1.5 text-xs font-sans font-medium text-clinic-ink hover:bg-clinic-pinkSoft hover:text-clinic-rose transition-colors"
                      >
                        {copied ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-emerald-700">Copiado!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5 text-clinic-muted" />
                            <span>Copiar endereço</span>
                          </>
                        )}
                      </button>
                      
                      <a
                        href={MAPS_DIRECTIONS}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-clinic-line bg-clinic-bg px-3 py-1.5 text-xs font-sans font-medium text-clinic-ink hover:bg-clinic-pinkSoft hover:text-clinic-rose transition-colors"
                      >
                        <Navigation className="h-3.5 w-3.5 text-clinic-rose" />
                        <span>Como chegar</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Hours */}
            <Reveal delay={0.25}>
              <div className="glass-card rounded-2xl border border-clinic-line/80 p-5 shadow-sm flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-clinic-pinkSoft text-clinic-rose">
                  <Clock className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-muted font-medium">
                    Horário de atendimento
                  </p>
                  <p className="mt-1 font-sans text-base text-clinic-ink font-medium">
                    {HOURS}
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Phone / WhatsApp */}
            <Reveal delay={0.35}>
              <div className="glass-card rounded-2xl border border-clinic-line/80 p-5 shadow-sm flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-clinic-pinkSoft text-clinic-rose">
                  <Phone className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-muted font-medium">
                    Telefone & WhatsApp
                  </p>
                  <a
                    href={PHONE_TEL}
                    data-testid="location-phone-link"
                    className="mt-1 block font-serif text-2xl font-medium text-clinic-ink hover:text-clinic-rose transition-colors"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.45}>
            <div className="mt-8">
              <WhatsappButton label="Agendar pelo WhatsApp" size="md" testId="location-whatsapp-cta" />
            </div>
          </Reveal>
        </div>

        {/* Right Google Maps Column */}
        <Reveal delay={0.2} className="lg:col-span-7">
          <div className="relative overflow-hidden rounded-[2rem] border-2 border-white shadow-soft-lg bg-clinic-pinkSoft/20">
            <iframe
              title="Mapa — Localização Clínica Lília Targa em Jundiaí"
              data-testid="location-map"
              src={MAPS_EMBED}
              width="100%"
              height="480"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block w-full"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

